import { QueryClient, QueryKey, UseQueryOptions } from "@tanstack/react-query";
import { ClientError, GraphQLClient } from "graphql-request";

import { graphql } from "lib/generated/app-service-gql";
import { checkIfStringIsGuid } from "lib/utils/signalUtils";
import {
  SupplierDetailsByIdQuery,
  SupplierDetailsByIdQueryVariables,
  SupplierDetailsQuery,
  SupplierDetailsQueryVariables,
} from "../../../generated/app-service-gql/graphql";
import { useGraphQlQuery } from "../useGraphQlClient";
import { asIdenticalResult, REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../utils";

const SUPPLIER_DETAILS_QUERY = graphql(`
  query supplierDetails($id: String!) {
    supplier(id: $id) {
      id
      guid
      name
      country
      countryCode
      isSme
      address
      town
      postalCode
      phone
      email
      isStotlesVerified
      isArchived
      createdAt
      updatedAt
      referenceOrganisations {
        name
        source
        sourceIdentifier
        address {
          address
          address2
          postCode
          countryCode
        }
        basicContactInfo {
          phone
          websiteUrl
        }
      }
      replacedBySuppliers {
        id
        name
        guid
      }
    }
  }
`);

const SUPPLIER_DETAILS_BY_ID_QUERY = graphql(`
  query supplierDetailsById($ids: [Int!]) {
    suppliers(SuppliersRequest: { ids: $ids }) {
      results {
        id
        guid
        name
        country
        countryCode
        isSme
        address
        town
        postalCode
        phone
        email
        isStotlesVerified
        isArchived
        createdAt
        updatedAt
        referenceOrganisations {
          name
          source
          sourceIdentifier
          address {
            address
            address2
            postCode
            countryCode
          }
          basicContactInfo {
            phone
            websiteUrl
          }
        }
        replacedBySuppliers {
          id
          name
          guid
        }
      }
      totalResults
    }
  }
`);

export type SupplierFromQuery = SupplierDetailsQuery["supplier"];

/**
 * Returns detailed information about a supplier
 * @param supplierGuid The GUID of the supplier to fetch
 * @param options Optional React Query options
 */
export function useSupplierDetails(
  supplierGuid: string,
  options?: UseQueryOptions<SupplierDetailsQuery, unknown, SupplierDetailsQuery, QueryKey>,
) {
  const { data, ...rest } = useGraphQlQuery(
    ["supplierDetails", supplierGuid],
    SUPPLIER_DETAILS_QUERY,
    [{ id: supplierGuid }],
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      ...options,
    },
  );

  return {
    data: data?.supplier,
    ...rest,
  };
}

/** Returns supplier details by numeric ID; used by TanStack supplier routes. */
export function useSupplierDetailsByNumericId(
  supplierId: number,
  options?: UseQueryOptions<SupplierDetailsByIdQuery, unknown, SupplierDetailsByIdQuery, QueryKey>,
) {
  const { data, ...rest } = useGraphQlQuery(
    ["supplierDetails", "id", supplierId],
    SUPPLIER_DETAILS_BY_ID_QUERY,
    [{ ids: [supplierId] }],
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      ...options,
    },
  );

  return {
    data: data?.suppliers.results,
    ...rest,
  };
}

/**
 * Single entry point for "fetch the supplier behind this URL", regardless of whether
 * the URL has a guid or a numeric id. Returns the same `SupplierFromQuery` shape in both
 * cases so callers don't have to branch.
 */
export function useSupplierLookup(idOrGuid: string) {
  const isGuid = checkIfStringIsGuid(idOrGuid);
  const numericId = parseInt(idOrGuid, 10);

  const guidQuery = useSupplierDetails(isGuid ? idOrGuid : "", { enabled: isGuid });
  const idQuery = useSupplierDetailsByNumericId(isGuid || isNaN(numericId) ? 0 : numericId, {
    enabled: !isGuid && !isNaN(numericId) && numericId > 0,
  });

  if (isGuid) {
    return {
      data: (guidQuery.data ?? null) as SupplierFromQuery | null,
      isLoading: guidQuery.isLoading,
    };
  }
  return {
    data: ((idQuery.data && idQuery.data[0]) ?? null) as SupplierFromQuery | null,
    isLoading: idQuery.isLoading,
  };
}

/** Seeds the exact-GUID key so a canonical-URL redirect target reads from cache. */
function seedCanonicalSupplier(queryClient: QueryClient, supplier: SupplierFromQuery) {
  queryClient.setQueryData<SupplierDetailsQuery>(["supplierDetails", supplier.guid], { supplier });
}

/**
 * Loader-side helper: prefetch the same supplier data the hook would fetch, sharing the
 * exact React Query cache key so the in-component hook reads from cache instead of refetching.
 * A hit on a non-canonical id (numeric, or a case-variant GUID) also seeds the exact-GUID key.
 * Returns null if no supplier was found (or if the request fails before auth is ready).
 */
export async function prefetchSupplierLookup(
  queryClient: QueryClient,
  client: GraphQLClient,
  idOrGuid: string,
): Promise<SupplierFromQuery | null> {
  if (checkIfStringIsGuid(idOrGuid)) {
    // Unlike the list query below, `supplier(id:)` throws on unknown guids. Map only that
    // case to null; transport/auth failures rethrow so the error boundary (and Sentry) see them.
    try {
      const data = await queryClient.ensureQueryData<SupplierDetailsQuery>({
        queryKey: ["supplierDetails", idOrGuid],
        queryFn: () =>
          client.request<SupplierDetailsQuery, SupplierDetailsQueryVariables>(
            SUPPLIER_DETAILS_QUERY,
            {
              id: idOrGuid,
            },
          ),
      });
      const supplier = data?.supplier ?? null;
      if (supplier && supplier.guid !== idOrGuid) seedCanonicalSupplier(queryClient, supplier);
      return supplier;
    } catch (error) {
      // HTTP 200 + GraphQL errors = the server executed the query and found no supplier.
      if (error instanceof ClientError && error.response.status === 200) {
        return null;
      }
      throw error;
    }
  }
  const numericId = parseInt(idOrGuid, 10);
  const data = await queryClient.ensureQueryData<SupplierDetailsByIdQuery>({
    queryKey: ["supplierDetails", "id", numericId],
    queryFn: () =>
      client.request<SupplierDetailsByIdQuery, SupplierDetailsByIdQueryVariables>(
        SUPPLIER_DETAILS_BY_ID_QUERY,
        { ids: [numericId] },
      ),
  });
  const byId = data?.suppliers?.results?.[0];
  if (!byId) return null;
  // Stops compiling if the two queries' selections drift apart.
  const supplier = asIdenticalResult<
    SupplierFromQuery,
    SupplierDetailsByIdQuery["suppliers"]["results"][number]
  >(byId);
  seedCanonicalSupplier(queryClient, supplier);
  return supplier;
}
